import { Injectable } from '@angular/core';
import { SessionService } from './session.service';

@Injectable({
    providedIn: 'root'
})
export class PhiWatermarkService {
    private isEnabled = false;
    private clockInterval: ReturnType<typeof setInterval> | null = null;
    private watermarkOverlayId = 'phi-watermark-overlay';
    private fullscreenChangeHandler: (() => void) | null = null;

    constructor(private sessionService: SessionService) { }

    /**
     * Enable the PHI watermark overlay
     * Displays a diagonal repeating pattern with PHI Protected Content, username, and real-time clock
     */
    enableWatermark(): void {
        if (this.isEnabled) {
            return; // Already enabled
        }

        this.isEnabled = true;
        this.createWatermarkOverlay();
        this.startClock();
        this.addFullscreenListener();
    }

    /**
     * Disable the PHI watermark overlay
     * Removes the overlay and stops the clock update
     */
    disableWatermark(): void {
        if (!this.isEnabled) {
            return; // Already disabled
        }

        this.isEnabled = false;
        this.stopClock();
        this.removeFullscreenListener();
        this.removeWatermarkOverlay();
    }

    /**
     * Toggle the watermark on/off
     */
    toggleWatermark(): void {
        if (this.isEnabled) {
            this.disableWatermark();
        } else {
            this.enableWatermark();
        }
    }

    /**
     * Check if watermark is currently enabled
     */
    isWatermarkEnabled(): boolean {
        return this.isEnabled;
    }

    // Create the watermark overlay element - Appends to fullscreenElement if available, otherwise to document.body
    private createWatermarkOverlay(): void {
        // Determine target element (fullscreen element or body)
        const targetElement = (document.fullscreenElement as HTMLElement) || document.body;

        // Check if overlay already exists
        const existing = document.getElementById(this.watermarkOverlayId);
        if (existing) {
            // If it exists but in a different parent (e.g. moved to/from fullscreen), move it
            if (existing.parentElement !== targetElement) {
                targetElement.appendChild(existing);
            }
            return;
        }

        const overlay = document.createElement('div');
        overlay.id = this.watermarkOverlayId;

        // Get username
        const username = this.getUsername();

        // Create watermark pattern
        overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 999997;
      overflow: hidden;
    `;

        // Create repeating watermark pattern
        const watermarkPattern = this.createWatermarkPattern(username);
        overlay.innerHTML = watermarkPattern;

        targetElement.appendChild(overlay);
    }

    // Create the repeating diagonal watermark pattern Dynamically calculates rows and columns to fill the entire screen
    private createWatermarkPattern(username: string): string {
        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Text dimensions (approximate width and height of watermark text when rotated)
        const textWidth = 350; // Approximate width of rotated text block
        const textHeight = 120; // Approximate height of rotated text block

        // Calculate spacing to ensure full coverage with overlap
        const horizontalSpacing = 280; // Reduced from 400 for tighter packing
        const verticalSpacing = 100; // Reduced from 150 for tighter packing

        // Calculate number of columns and rows needed to fill screen
        // Add extra to account for rotation and ensure no gaps
        const cols = Math.ceil(viewportWidth / horizontalSpacing) + 3;
        const rows = Math.ceil(viewportHeight / verticalSpacing) + 3;

        let pattern = '';

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const top = row * verticalSpacing - 100; // Start above viewport
                const left = col * horizontalSpacing - 100; // Start left of viewport

                pattern += `
          <div style="
            position: absolute;
            top: ${top}px;
            left: ${left}px;
            transform: rotate(-45deg);
            opacity: 0.15;
            user-select: none;
            pointer-events: none;
            font-family: 'Poppins', sans-serif;
            font-weight: 600;
            font-size: 18px;
            color: #333;
            white-space: nowrap;
            text-align: center;
            line-height: 1.6;
          ">
            <div style="margin-top: 3px; margin-bottom: 2px; font-size: 20px; font-weight: 700;">🔒 PHI PROTECTED CONTENT</div>
            <div style="font-size: 16px; color: #555;">User: ${username}</div>
            <div id="watermark-time-${row}-${col}" style="font-size: 14px; color: #666;"></div>
          </div>
        `;
            }
        }

        return pattern;
    }

    /**
     * Remove the watermark overlay
     */
    private removeWatermarkOverlay(): void {
        const overlay = document.getElementById(this.watermarkOverlayId);
        if (overlay) {
            overlay.remove();
        }
    }

    /**
     * Start the real-time clock update
     */
    private startClock(): void {
        // Update immediately
        this.updateClock();

        // Update every second
        this.clockInterval = setInterval(() => {
            this.updateClock();
        }, 1000);
    }

    /**
     * Stop the clock update interval
     */
    private stopClock(): void {
        if (this.clockInterval) {
            clearInterval(this.clockInterval);
            this.clockInterval = null;
        }
    }

    /**
     * Update all clock displays in the watermark
     */
    private updateClock(): void {
        const now = new Date();
        const timeString = this.formatDateTime(now);

        // Update all time elements
        const overlay = document.getElementById(this.watermarkOverlayId);
        if (overlay) {
            const timeElements = overlay.querySelectorAll('[id^="watermark-time-"]');
            timeElements.forEach((element) => {
                element.textContent = timeString;
            });
        }
    }

    /**
     * Format date and time for display
     */
    private formatDateTime(date: Date): string {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12;
        hours = hours ? hours : 12; // 0 should be 12
        const hoursStr = String(hours).padStart(2, '0');

        return `${month}/${day}/${year} ${hoursStr}:${minutes}:${seconds} ${ampm}`;
    }

    /**
     * Get the current logged-in username
     */
    private getUsername(): string {
        try {
            const username = this.sessionService.getUsername();
            return username || 'Unknown User';
        } catch (error) {
            console.error('Error getting username for watermark:', error);
            return 'Unknown User';
        }
    }

    /**
     * Add fullscreen change event listener
     * Moves the watermark overlay when entering/exiting fullscreen
     */
    private addFullscreenListener(): void {
        this.fullscreenChangeHandler = () => {
            if (this.isEnabled) {
                // Re-create overlay to move it to the correct parent
                this.createWatermarkOverlay();
            }
        };

        document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
        document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
        document.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler);
        document.addEventListener('MSFullscreenChange', this.fullscreenChangeHandler);
    }

    /**
     * Remove fullscreen change event listener
     */
    private removeFullscreenListener(): void {
        if (this.fullscreenChangeHandler) {
            document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
            document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
            document.removeEventListener('mozfullscreenchange', this.fullscreenChangeHandler);
            document.removeEventListener('MSFullscreenChange', this.fullscreenChangeHandler);
            this.fullscreenChangeHandler = null;
        }
    }
}
